import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const BrokerOnboarding = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    license_number: '',
    license_docs: [],
    specialties: []
  });
  const [loading, setLoading] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSpecialtyToggle = (specialty) => {
    const current = formData.specialties;
    if (current.includes(specialty)) {
      setFormData({ ...formData, specialties: current.filter(s => s !== specialty) });
    } else {
      setFormData({ ...formData, specialties: [...current, specialty] });
    }
  };

  const handleSubmit = async () => {
    if (!formData.license_number) {
      alert('Please enter your license number');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/partner/onboard`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        const data = await res.json();
        if (data.kyc_passed) {
          alert('✓ Partner onboarding complete! KYC verified. Redirecting...');
          
          // Update user state in localStorage
          const userStr = localStorage.getItem('user');
          if (userStr) {
            const user = JSON.parse(userStr);
            user.onboarding_complete = true;
            localStorage.setItem('user', JSON.stringify(user));
          }
          
          // Redirect to partner dashboard
          setTimeout(() => {
            window.location.href = '/broker/dashboard';
          }, 500);
        }
      } else {
        alert('Onboarding failed. Please try again.');
      }
    } catch (error) {
      console.error('Error onboarding partner:', error);
      alert('Error onboarding partner');
    } finally {
      setLoading(false);
    }
  };

  const specialtiesOptions = [
    'Streetwear', 'Luxury Fashion', 'Sustainable Fashion', 
    'High Fashion', 'Accessories', 'Footwear'
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-gray-800 rounded-lg p-8">
        <h1 className="text-3xl font-bold text-blue-400 mb-2">Partner Onboarding</h1>
        <p className="text-gray-400 mb-6">Complete your profile to start managing portfolios</p>

        {/* Progress Steps */}
        <div className="flex justify-between mb-8">
          <div className={`flex-1 text-center pb-2 border-b-2 ${step >= 1 ? 'border-blue-500' : 'border-gray-700'}`}>
            <span className={step >= 1 ? 'text-blue-400' : 'text-gray-500'}>1. License</span>
          </div>
          <div className={`flex-1 text-center pb-2 border-b-2 ${step >= 2 ? 'border-blue-500' : 'border-gray-700'}`}>
            <span className={step >= 2 ? 'text-blue-400' : 'text-gray-500'}>2. Specialties</span>
          </div>
          <div className={`flex-1 text-center pb-2 border-b-2 ${step >= 3 ? 'border-blue-500' : 'border-gray-700'}`}>
            <span className={step >= 3 ? 'text-blue-400' : 'text-gray-500'}>3. KYC</span>
          </div>
        </div>

        {/* Step 1: License */}
        {step === 1 && (
          <div>
            <h2 className="text-xl font-bold mb-4">Broker License Information</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">License Number *</label>
                <input
                  type="text"
                  name="license_number"
                  value={formData.license_number}
                  onChange={handleInputChange}
                  className="w-full p-3 bg-gray-700 rounded border border-gray-600 focus:border-blue-500 outline-none"
                  placeholder="e.g., BRK-2024-123456"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Upload License Documents (Optional)</label>
                <div className="border-2 border-dashed border-gray-600 rounded p-6 text-center text-gray-500">
                  <p>Drag & drop files here or click to browse</p>
                  <p className="text-xs mt-2">(Mock upload - not functional in MVP)</p>
                </div>
              </div>
            </div>
            <div className="flex justify-end mt-6">
              <button
                onClick={() => setStep(2)}
                disabled={!formData.license_number}
                className="px-6 py-2 bg-blue-600 rounded hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Specialties */}
        {step === 2 && (
          <div>
            <h2 className="text-xl font-bold mb-4">Your Specialties</h2>
            <p className="text-gray-400 text-sm mb-4">Select areas you specialize in</p>
            <div className="grid grid-cols-2 gap-3 mb-6">
              {specialtiesOptions.map((specialty) => (
                <button
                  key={specialty}
                  onClick={() => handleSpecialtyToggle(specialty)}
                  className={`p-3 rounded border ${
                    formData.specialties.includes(specialty)
                      ? 'bg-blue-600 border-blue-500'
                      : 'bg-gray-700 border-gray-600 hover:border-gray-500'
                  }`}
                >
                  {specialty}
                </button>
              ))}
            </div>
            <div className="flex justify-between mt-6">
              <button
                onClick={() => setStep(1)}
                className="px-6 py-2 bg-gray-700 rounded hover:bg-gray-600"
              >
                Back
              </button>
              <button
                onClick={() => setStep(3)}
                className="px-6 py-2 bg-blue-600 rounded hover:bg-blue-700"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Step 3: KYC */}
        {step === 3 && (
          <div>
            <h2 className="text-xl font-bold mb-4">KYC Verification</h2>
            <div className="bg-gray-700 p-6 rounded mb-6">
              <p className="text-gray-300 mb-4">
                We'll verify your identity through our partner <span className="text-blue-400 font-semibold">Persona (Mock)</span>
              </p>
              <div className="space-y-2 text-sm text-gray-400">
                <p>✓ License Number: {formData.license_number}</p>
                <p>✓ Specialties: {formData.specialties.join(', ') || 'None selected'}</p>
              </div>
            </div>
            <div className="bg-yellow-900/30 border border-yellow-600 rounded p-4 mb-6">
              <p className="text-yellow-400 text-sm">
                <strong>Note:</strong> This is a mock KYC process. In production, this would integrate with Persona API.
              </p>
            </div>
            <div className="flex justify-between mt-6">
              <button
                onClick={() => setStep(2)}
                className="px-6 py-2 bg-gray-700 rounded hover:bg-gray-600"
              >
                Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-6 py-2 bg-green-600 rounded hover:bg-green-700 disabled:bg-gray-600"
              >
                {loading ? 'Processing...' : 'Complete Onboarding'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BrokerOnboarding;
